import { useState } from 'react';
import { X, Linkedin, Globe, User, Building2, MapPin, ChevronDown, ChevronUp } from 'lucide-react';
import { leadsApi } from '../../services/leads.api';
import { ICP_TYPES, ASSIGNEES } from '../../lib/constants';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const INDUSTRIES = ['SaaS / Tech', 'Healthcare', 'Fintech', 'Logistics / Supply Chain', 'Retail / E-commerce', 'Manufacturing', 'Education / EdTech', 'Real Estate', 'Hospitality', 'Other'];
const PROJECT_TYPES = ['Web App', 'Mobile App', 'ERP / CRM', 'API / Backend', 'UI/UX Design', 'Full-Stack Product', 'AI / Automation', 'Other'];

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-xs font-medium text-zinc-600 mb-1">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const Input = ({ className, ...props }) => (
  <input className={cn('w-full text-sm border border-zinc-200 rounded-md px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white', className)} {...props} />
);

const Select = ({ className, children, ...props }) => (
  <select className={cn('w-full text-sm border border-zinc-200 rounded-md px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white', className)} {...props}>
    {children}
  </select>
);

export default function AddLeadModal({ onClose, onAdded }) {
  const [tab, setTab] = useState('prospect'); // 'prospect' | 'sales'
  const [loading, setLoading] = useState(false);
  const [showOptional, setShowOptional] = useState(false);
  const [form, setForm] = useState({
    // Core
    company_name: '', contact_name: '', designation: '', linkedin_url: '',
    email: '', country: '', industry: '', icp_type: '', company_size: '',
    website: '',
    // Outreach tracking (Nayema)
    connection_sent_date: '', connection_accepted: '', message_sent_date: '',
    reply_received: '', reply_summary: '', qualified: '',
    // Sales (Divyanshu)
    assigned_to: '', call_date: '', budget: '', proposal_value: '',
    retainer_monthly: '', expected_close_date: '', pain_point: '',
    outreach_angle: '', notes: '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim()) { toast.error('Company name is required'); return; }
    setLoading(true);
    try {
      const { data } = await leadsApi.create(form);
      toast.success(`Lead added: ${data.company_name}`);
      onAdded(data);
      onClose();
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error('Duplicate: a lead with this company name already exists');
      } else {
        toast.error('Failed to add lead');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="flex-1 bg-black/40" onClick={onClose} />
      {/* Drawer */}
      <div className="bg-white shadow-2xl w-full max-w-[75vw] h-full flex flex-col animate-in slide-in-from-right duration-200">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
          <h2 className="text-base font-semibold text-zinc-900">Add New Lead</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700"><X size={18} /></button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-zinc-200 px-5">
          {[['prospect', 'Prospecting (Nayema)'], ['sales', 'Sales Info (Divyanshu)']].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)}
              className={cn('py-2.5 px-4 text-sm font-medium border-b-2 -mb-px transition-colors',
                tab === id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-zinc-500 hover:text-zinc-700')}>
              {label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

          {tab === 'prospect' && (
            <>
              {/* Required fields */}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Company Name" required>
                  <Input placeholder="Acme Corp" value={form.company_name} onChange={e => set('company_name', e.target.value)} required />
                </Field>
                <Field label="Contact Name" required>
                  <Input placeholder="John Smith" value={form.contact_name} onChange={e => set('contact_name', e.target.value)} />
                </Field>
                <Field label="LinkedIn URL">
                  <div className="relative">
                    <Linkedin size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <Input className="pl-7" placeholder="linkedin.com/in/..." value={form.linkedin_url} onChange={e => set('linkedin_url', e.target.value)} />
                  </div>
                </Field>
                <Field label="Designation / Title">
                  <Input placeholder="CTO / Founder / Head of Ops" value={form.designation} onChange={e => set('designation', e.target.value)} />
                </Field>
                <Field label="Country" required>
                  <Input placeholder="UAE / UK / US / India..." value={form.country} onChange={e => set('country', e.target.value)} />
                </Field>
                <Field label="ICP Type" required>
                  <Select value={form.icp_type} onChange={e => set('icp_type', e.target.value)}>
                    <option value="">Select ICP...</option>
                    {ICP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </Select>
                </Field>
                <Field label="Industry">
                  <Select value={form.industry} onChange={e => set('industry', e.target.value)}>
                    <option value="">Select industry...</option>
                    {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                  </Select>
                </Field>
                <Field label="Email">
                  <Input type="email" placeholder="john@acmecorp.com" value={form.email} onChange={e => set('email', e.target.value)} />
                </Field>
              </div>

              {/* Outreach tracking */}
              <div className="border-t border-zinc-100 pt-3">
                <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-3">Outreach Tracking</p>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Connection Sent Date">
                    <Input type="date" value={form.connection_sent_date} onChange={e => set('connection_sent_date', e.target.value)} />
                  </Field>
                  <Field label="Connection Accepted">
                    <Select value={form.connection_accepted} onChange={e => set('connection_accepted', e.target.value)}>
                      <option value="">Select...</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                      <option value="Pending">Pending</option>
                    </Select>
                  </Field>
                  <Field label="Message 1 Sent Date">
                    <Input type="date" value={form.message_sent_date} onChange={e => set('message_sent_date', e.target.value)} />
                  </Field>
                  <Field label="Reply Received">
                    <Select value={form.reply_received} onChange={e => set('reply_received', e.target.value)}>
                      <option value="">Select...</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </Select>
                  </Field>
                  <Field label="Qualified">
                    <Select value={form.qualified} onChange={e => set('qualified', e.target.value)}>
                      <option value="">Select...</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                      <option value="Maybe">Maybe</option>
                    </Select>
                  </Field>
                  <Field label="Reply Summary">
                    <Input placeholder="What they said..." value={form.reply_summary} onChange={e => set('reply_summary', e.target.value)} />
                  </Field>
                </div>
              </div>

              {/* Optional fields toggle */}
              <button type="button" onClick={() => setShowOptional(s => !s)}
                className="flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-600 pt-1">
                {showOptional ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                {showOptional ? 'Hide' : 'Show'} optional fields
              </button>
              {showOptional && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Company Size">
                    <Input placeholder="50-200 employees" value={form.company_size} onChange={e => set('company_size', e.target.value)} />
                  </Field>
                  <Field label="Website">
                    <Input placeholder="https://..." value={form.website} onChange={e => set('website', e.target.value)} />
                  </Field>
                  <div className="col-span-2">
                    <Field label="Notes">
                      <textarea rows={2} className="w-full text-sm border border-zinc-200 rounded-md px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
                        placeholder="Anything else relevant..." value={form.notes} onChange={e => set('notes', e.target.value)} />
                    </Field>
                  </div>
                </div>
              )}
            </>
          )}

          {tab === 'sales' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Assigned To">
                  <Select value={form.assigned_to} onChange={e => set('assigned_to', e.target.value)}>
                    <option value="">Unassigned</option>
                    {ASSIGNEES.map(a => <option key={a} value={a}>{a}</option>)}
                  </Select>
                </Field>
                <Field label="Discovery Call Date">
                  <Input type="date" value={form.call_date} onChange={e => set('call_date', e.target.value)} />
                </Field>
                <Field label="Client Budget">
                  <Input placeholder="$5,000 / Rs 5L" value={form.budget} onChange={e => set('budget', e.target.value)} />
                </Field>
                <Field label="Our Proposal Value">
                  <Input placeholder="$6,000 / Rs 6L" value={form.proposal_value} onChange={e => set('proposal_value', e.target.value)} />
                </Field>
                <Field label="Monthly Retainer">
                  <Input placeholder="$200/month / Rs 10k/month" value={form.retainer_monthly} onChange={e => set('retainer_monthly', e.target.value)} />
                </Field>
                <Field label="Expected Close Date">
                  <Input type="date" value={form.expected_close_date} onChange={e => set('expected_close_date', e.target.value)} />
                </Field>
                <div className="col-span-2">
                  <Field label="Pain Point / Why They Need Us">
                    <textarea rows={2} className="w-full text-sm border border-zinc-200 rounded-md px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
                      placeholder="What problem are they solving..." value={form.pain_point} onChange={e => set('pain_point', e.target.value)} />
                  </Field>
                </div>
                <div className="col-span-2">
                  <Field label="Outreach / Opening Message">
                    <textarea rows={2} className="w-full text-sm border border-zinc-200 rounded-md px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
                      placeholder="What message was sent or what angle to use..." value={form.outreach_angle} onChange={e => set('outreach_angle', e.target.value)} />
                  </Field>
                </div>
              </div>
            </>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-zinc-200 bg-zinc-50">
          <p className="text-xs text-zinc-400">Lead will be saved with status: <strong>New</strong></p>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="text-sm text-zinc-500 hover:text-zinc-700 px-4 py-1.5">Cancel</button>
            <button onClick={submit} disabled={loading}
              className="text-sm bg-indigo-600 text-white px-5 py-1.5 rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium">
              {loading ? 'Saving...' : 'Add Lead'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
